/**
 * Hud.ts
 * -----------------------------------------------------------------------------
 * One-line performance HUD pinned to the bottom-right of the 3D viewport:
 *
 *     FPS 60  ·  CPU 128 draws  ·  GPU 1.24M tris
 *
 *   - FPS  : frames per second, averaged over a ~0.5 s window.
 *   - CPU  : draw calls last frame (renderer.info.render.calls). Each draw call
 *            is CPU-side submission overhead, so this is the CPU draw cost.
 *   - GPU  : triangles last frame (renderer.info.render.triangles) — the
 *            primitives the GPU actually rasterizes, i.e. the GPU draw load.
 *
 * Both counters come straight from Three.js's renderer.info (no extra GPU work).
 * Driven by Viewport3D.OnFrame so it ticks with the render loop and stops when
 * the loop is paused/disposed.
 */

import type { Viewport3D } from './Viewport3D';

export class Hud {
  private readonly el: HTMLDivElement;
  private readonly viewport: Viewport3D;
  private readonly unsubscribe: () => void;

  private frames = 0;
  private elapsed = 0; // seconds accumulated since last refresh
  private fps = 0;
  private calls = 0;
  private triangles = 0;

  constructor(viewport: Viewport3D) {
    this.viewport = viewport;

    this.el = document.createElement('div');
    this.el.className = 'bridge-hud';
    viewport.container.appendChild(this.el);

    this.unsubscribe = viewport.OnFrame((dt) => this.onFrame(dt));
    this.draw();
  }

  private onFrame(dt: number): void {
    this.frames++;
    this.elapsed += dt;

    if (this.elapsed >= 0.5) {
      this.fps = this.frames / this.elapsed;
      // renderer.info.render holds the last completed frame's counters (it's
      // reset at the start of the next render(), which runs after this callback).
      const info = this.viewport.renderer.info.render;
      this.calls = info.calls;
      this.triangles = info.triangles;

      this.frames = 0;
      this.elapsed = 0;
      this.draw();
    }
  }

  private draw(): void {
    this.el.textContent =
      `FPS ${this.fps.toFixed(0)}  ·  CPU ${compact(this.calls)} draws  ·  GPU ${compact(this.triangles)} tris`;
  }

  dispose(): void {
    this.unsubscribe();
    if (this.el.parentElement) this.el.parentElement.removeChild(this.el);
  }
}

/** Compact integer formatting: 1234567 → "1.23M", 12345 → "12.3k", 999 → "999". */
function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
